const WORD_CHARACTER_PATTERN = /[\p{L}\p{N}]/u;
const UPPERCASE_CHARACTER_PATTERN = /\p{Lu}/u;
const LOWERCASE_CHARACTER_PATTERN = /\p{Ll}/u;
const LEADING_PUNCTUATION_PATTERN = /^[,.;:!?)]/;
const TRAILING_JOINER_PATTERN = /[(\[{/\-]$/;
const NUMBERED_STEP_START_PATTERN = /^[\])\]}]*(\d+[.)])(?:\s+|$)/;
const IMPLICIT_STEP_START_PATTERN =
  /^(?:[-*]\s+|Using tool:\s*|Executing\b|Processing\b|Initial\b|Results\b|However\b|I should\b|All data\b|Here is\b)/i;

function isNumberedStepStart(text: string): boolean {
  return NUMBERED_STEP_START_PATTERN.test(text);
}

function normalizeReasoningLineStart(text: string): string {
  return text.replace(/^[\])\]}]+(?=\d+[.)](?:\s+|$))/, '');
}

function startsWithWordCharacter(text: string): boolean {
  return WORD_CHARACTER_PATTERN.test(text.charAt(0));
}

function endsWithWordCharacter(text: string): boolean {
  return WORD_CHARACTER_PATTERN.test(text.charAt(text.length - 1));
}

function hasAcronymFragmentBoundary(previousText: string, nextText: string): boolean {
  const previousTokenMatch = previousText.match(/[\p{L}\p{N}]+$/u);
  const nextTokenMatch = nextText.match(/^[\p{L}\p{N}]+/u);

  if (!previousTokenMatch || !nextTokenMatch) {
    return false;
  }

  const previousToken = previousTokenMatch[0];
  const nextToken = nextTokenMatch[0];
  const areUppercaseFragments = /^[\p{Lu}\p{N}]+$/u.test(previousToken) && /^[\p{Lu}\p{N}]+$/u.test(nextToken);

  if (previousToken.length === 1 && /^[\p{Lu}\p{N}]{2,}/u.test(nextToken)) {
    return true;
  }

  return areUppercaseFragments && (previousToken.length === 1 || nextToken.length === 1);
}

function needsInsertedSpace(previousText: string, nextText: string): boolean {
  if (!previousText || !nextText) {
    return false;
  }

  if (/\s$/.test(previousText) || /^\s/.test(nextText)) {
    return false;
  }

  if (previousText.endsWith('\n') || nextText.startsWith('\n')) {
    return false;
  }

  if (LEADING_PUNCTUATION_PATTERN.test(nextText) || TRAILING_JOINER_PATTERN.test(previousText)) {
    return false;
  }

  if (hasAcronymFragmentBoundary(previousText, nextText)) {
    return false;
  }

  return endsWithWordCharacter(previousText) && startsWithWordCharacter(nextText);
}

function isLikelyLowercaseWordContinuation(previousText: string, nextText: string): boolean {
  if (!previousText || !nextText) {
    return false;
  }

  return (
    LOWERCASE_CHARACTER_PATTERN.test(previousText.charAt(previousText.length - 1)) &&
    LOWERCASE_CHARACTER_PATTERN.test(nextText.charAt(0))
  );
}

function shouldStartNewReasoningLine(previousText: string, nextText: string): boolean {
  if (!previousText || !nextText) {
    return false;
  }

  const trimmedPreviousText = previousText.trimEnd();
  const trimmedNextText = nextText.trimStart();

  if (!trimmedNextText) {
    return false;
  }

  if (/^\s/.test(nextText) || LEADING_PUNCTUATION_PATTERN.test(trimmedNextText)) {
    return false;
  }

  if (TRAILING_JOINER_PATTERN.test(trimmedPreviousText) || hasAcronymFragmentBoundary(previousText, nextText)) {
    return false;
  }

  if (isNumberedStepStart(trimmedNextText) || IMPLICIT_STEP_START_PATTERN.test(trimmedNextText)) {
    return true;
  }

  if (trimmedPreviousText.includes('Parsing filters:') && UPPERCASE_CHARACTER_PATTERN.test(trimmedNextText.charAt(0))) {
    return true;
  }

  return /[:.!?]$/.test(trimmedPreviousText) && UPPERCASE_CHARACTER_PATTERN.test(trimmedNextText.charAt(0));
}

function shouldPreserveExplicitNewline(previousText: string, nextText: string): boolean {
  const trimmedPreviousText = previousText.trimEnd();
  const normalizedNextText = normalizeReasoningLineStart(nextText.trimStart());

  if (!trimmedPreviousText || !normalizedNextText) {
    return true;
  }

  if (isNumberedStepStart(normalizedNextText) || IMPLICIT_STEP_START_PATTERN.test(normalizedNextText)) {
    return true;
  }

  if (/[:.!?]$/.test(trimmedPreviousText)) {
    return true;
  }

  return !LOWERCASE_CHARACTER_PATTERN.test(normalizedNextText.charAt(0));
}

function shouldMergeWrappedReasoningLine(previousText: string, nextText: string): boolean {
  const trimmedPreviousText = previousText.trimEnd();
  const normalizedNextText = normalizeReasoningLineStart(nextText.trimStart());

  if (!trimmedPreviousText || !normalizedNextText) {
    return false;
  }

  if (isNumberedStepStart(normalizedNextText) || IMPLICIT_STEP_START_PATTERN.test(normalizedNextText)) {
    return false;
  }

  if (/[:.!?]$/.test(trimmedPreviousText)) {
    return false;
  }

  return LOWERCASE_CHARACTER_PATTERN.test(normalizedNextText.charAt(0));
}

function normalizeWrappedReasoningLines(lines: ReadonlyArray<string>): ReadonlyArray<string> {
  return lines.reduce<string[]>((result, line) => {
    const normalizedLine = normalizeReasoningLineStart(line);
    const previousLine = result.at(-1);

    if (previousLine && shouldMergeWrappedReasoningLine(previousLine, normalizedLine)) {
      result[result.length - 1] =
        `${previousLine}${needsInsertedSpace(previousLine, normalizedLine) ? ' ' : ''}${normalizedLine}`;
      return result;
    }

    result.push(normalizedLine);
    return result;
  }, []);
}

export function stitchReasoningChunks(reasoningSteps: ReadonlyArray<string>): string {
  const stitchedLines: string[] = [];
  let currentLine = '';

  const flushCurrentLine = () => {
    const trimmedLine = currentLine.trim();

    if (trimmedLine) {
      stitchedLines.push(trimmedLine);
    }

    currentLine = '';
  };

  const appendSegment = (segment: string) => {
    if (!segment) {
      return;
    }

    const normalizedSegment = normalizeReasoningLineStart(segment);

    if (!currentLine) {
      currentLine = normalizedSegment.trimStart();
      return;
    }

    if (shouldStartNewReasoningLine(currentLine, normalizedSegment)) {
      flushCurrentLine();
      currentLine = normalizedSegment.trimStart();
      return;
    }

    const shouldInsertSpace =
      needsInsertedSpace(currentLine, normalizedSegment) &&
      !isLikelyLowercaseWordContinuation(currentLine, normalizedSegment);

    if (shouldInsertSpace) {
      currentLine += ' ';
    }

    currentLine += normalizedSegment;
  };

  for (const chunk of reasoningSteps) {
    const normalizedChunk = chunk.replace(/\r\n/g, '\n');

    if (!normalizedChunk) {
      continue;
    }

    const segments = normalizedChunk.split('\n');

    for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex += 1) {
      const segment = segments[segmentIndex];
      appendSegment(segment);

      const nextSegment = segments[segmentIndex + 1];

      if (nextSegment !== undefined && shouldPreserveExplicitNewline(currentLine, nextSegment)) {
        flushCurrentLine();
      }
    }
  }

  flushCurrentLine();

  return normalizeWrappedReasoningLines(stitchedLines).join('\n');
}
